import React, { useState, useEffect, useMemo } from 'react';

// Comps
import { CustomFields } from '@project-stories/ecommerce/Account/AccountFields/AccountFields';
import {InputField} from '@project-stories/elements/InputField/InputField';
import {Paragraph} from '@project-stories/elements/Paragraph/Paragraph';
import AccountSectionLayout from '@project-stories/ecommerce/Account/AccountSectionLayout';
import ButtonSpinner from '@project-stories/elements/ButtonSpinner/ButtonSpinner';

// Hooks
import { useGetCustomerQuery } from '@src/hooks/useGetCustomerQuery';
import useCustomerUpdate from '@src/hooks/useCustomerUpdate';
import {Button} from "@src/stories/elements/Button/Button";
import { initializePasswordResetForm } from '@src/actions/runCustomerForgotPassword';

const AccountProfileForm = ({ section, signUpFields }) => {
  const {
    first_name_label = 'First Name',
    first_name_placeholder = 'Enter your first name',
    last_name_label = 'Last Name',
    last_name_placeholder = 'Enter your last name',
    email_label = 'Email',
    email_placeholder = 'Enter your email',
    phone_label = 'Phone',
    phone_placeholder = 'Enter your phone number',
    password_label = 'Password',
    password_placeholder = 'Enter new password',
    password_confirm_label = 'Confirm Password',
    password_confirmation_placeholder = 'Confirm your new password',
    button_label = 'Update Profile',
    button_style = 'primary',
    success_message = 'Profile updated successfully'
  } = section || {};

  // Sort custom fields
  const customSignUpFields = signUpFields?.sort((a, b) => {
    const orderA = a.field_order ? parseInt(a.field_order) : Infinity;
    const orderB = b.field_order ? parseInt(b.field_order) : Infinity;
    return orderA - orderB;
  });

  // Hooks
  const { fetchCustomer, customer } = useGetCustomerQuery();
  const {
    updateCustomer,
    loading: updateCustomerLoading,
    success: updateCustomerSuccess,
    error: updateCustomerError,
    fieldsWithError: updateCustomerFieldsWithError
  } = useCustomerUpdate();

  // States
  const initialDependenciesState = useMemo(() => {
    return Object.assign(
      {},
      {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        passwordConfirmation: ''
      },
      ...customSignUpFields.map((field) => ({
        [field.metafield_key]: ''
      }))
    );
  }, [customer]);
  const [dependenciesState, setDependenciesState]: any = useState(initialDependenciesState);
  const [isUpdated, setIsUpdated] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordConfirmError, setPasswordConfirmError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [hasNewChangesAfterSuccess, setHasNewChangesAfterSuccess] = useState(false);

  // Handle input change and update global dependency state
  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;

    // For checkboxes, use the checked value, otherwise use the input value
    const newValue = inputType === 'checkbox' ? checked : value;

    // Update the global dependency state
    updateDependencyState(name, newValue);
  };

  // Function to update the global dependencies state
  const updateDependencyState = (name, value) => {
    const newData = {
      ...dependenciesState,
      [name]: value
    };

    setDependenciesState(newData);
    toggleIsUpdated(newData);
    
    // If we had a successful save and now user is making new changes, reset the success state
    if (updateCustomerSuccess) {
      setHasNewChangesAfterSuccess(true);
    }
    
    // Password validation
    if (name === 'password' || name === 'passwordConfirmation') {
      validatePasswords(newData);
    }
    
    // Phone validation
    if (name === 'phone') {
      validatePhone(value);
    }
  };

  // Password validation function
  const validatePasswords = (data = dependenciesState) => {
    const password = data.password || '';
    const passwordConfirmation = data.passwordConfirmation || '';
    
    // Clear previous errors
    setPasswordError('');
    setPasswordConfirmError('');
    
    // If both passwords are entered, they must match
    if (password && passwordConfirmation && password !== passwordConfirmation) {
      setPasswordConfirmError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  // Phone validation function
  const validatePhone = (phoneNumber) => {
    // If phone is empty, it's valid (not required)
    if (!phoneNumber) {
      setPhoneError('');
      return true;
    }
    
    // Check if phone starts with + and has exactly 11 digits after
    const phoneRegex = /^\+\d{11}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setPhoneError('Phone number must be in format +00000000000 (+ followed by 11 digits)');
      return false;
    }
    
    // Clear error if validation passes
    setPhoneError('');
    return true;
  };

  const toggleIsUpdated = (newData) => {
    const updated = Object.keys(newData).some((key) => customer[key] !== newData[key]);

    setIsUpdated(updated);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate passwords before submission
    if (!validatePasswords()) {
      return;
    }

    // Validate phone before submission
    if (!validatePhone(dependenciesState.phone)) {
      return;
    }
    
    // Prepare customer data (remove passwordConfirmation as it's not needed for the API)
    const { passwordConfirmation, ...customerData } = dependenciesState;
    
    // Only include password if it's not empty
    if (!customerData.password) {
      delete customerData.password;
    }
    
    updateCustomer(customerData);
    // Don't exit edit mode here - let the user decide when to go back
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      // If currently in edit mode and clicking save, submit the form
      const form = document.getElementById('editForm') as HTMLFormElement;
      if (form) {
        form.requestSubmit();
      }
    } else {
      // Switch to edit mode
      setIsEditMode(true);
    }
  };

  // Fetch customer
  useEffect(() => {
    fetchCustomer((window as any).customer.id, customSignUpFields);
  }, []);

  // And then pre-fill the fields
  useEffect(() => {
    if (customer) {
      for (const key in customer) {
        if (key in dependenciesState) {
          setDependenciesState((prev) => ({
            ...prev,
            [key]: customer[key]
          }));
        }
      }
    }
  }, [customer]);

  // Handle successful update - stay in edit mode but clear update flags
  useEffect(() => {
    if (updateCustomerSuccess) {
      // Don't exit edit mode automatically - let user decide
      setIsUpdated(false);
      setPasswordError('');
      setPasswordConfirmError('');
      setPhoneError('');
      setHasNewChangesAfterSuccess(false); // Reset the flag when we get a new success
      
      // Clear password fields after successful update
      setDependenciesState(prev => ({
        ...prev,
        password: '',
        passwordConfirmation: ''
      }));
      
      // Refresh customer data to get updated info
      setTimeout(() => {
        fetchCustomer((window as any).customer.id, customSignUpFields);
      }, 1000);
    }
  }, [updateCustomerSuccess]);

  const showCustomFields = true;

  // Read-only profile display component
  const ProfileDisplay = () => (
    <div className='flex flex-col gap-y-4'>
      <div className='flex flex-col gap-y-2'>
        <label className='text-sm font-medium text-gray-700'>{first_name_label}</label>
        <div className='p-3 bg-gray-50 rounded-[12px] border'>
          {dependenciesState['firstName'] || 'Not provided'}
        </div>
      </div>

      <div className='flex flex-col gap-y-2'>
        <label className='text-sm font-medium text-gray-700'>{last_name_label}</label>
        <div className='p-3 bg-gray-50 rounded-[12px] border'>
          {dependenciesState['lastName'] || 'Not provided'}
        </div>
      </div>

      <div className='flex flex-col gap-y-2'>
        <label className='text-sm font-medium text-gray-700'>{email_label}</label>
        <div className='p-3 bg-gray-50 rounded-[12px] border'>
          {dependenciesState['email'] || 'Not provided'}
        </div>
      </div>

      <div className='flex flex-col gap-y-2'>
        <label className='text-sm font-medium text-gray-700'>{phone_label}</label>
        <div className='p-3 bg-gray-50 rounded-[12px] border'>
          {dependenciesState['phone'] || 'Not provided'}
        </div>
      </div>

      {/* Custom Fields Display */}
      {showCustomFields && customSignUpFields?.map((field) => (
        <div key={field.metafield_key} className='flex flex-col gap-y-2'>
          <label className='text-sm font-medium text-gray-700'>{field.field_label}</label>
          <div className='p-3 bg-gray-50 rounded-[12px] border'>
            {dependenciesState[field.metafield_key] || 'Not provided'}
          </div>
        </div>
      ))}

      <Button
        className={`w-full md:max-w-[200px] button-${button_style}`}
        onClick={() => setIsEditMode(true)}
        aria-label='Edit profile'
        label='Edit Profile'
      />
    </div>
  );

  return (
    <AccountSectionLayout classes='hide-scrollbar'>
      <div className={'flex flex-col lg:flex-row flex-wrap gap-[41px] w-full'}>
        <div className={'flex flex-col w-full'}>
          {!isEditMode ? (
            <div className='pt-[16px]'>
              <ProfileDisplay />
            </div>
          ) : (
            <form
              id='editForm'
              onSubmit={handleSubmit}
              className='account-page__form pt-[16px]'
            >
              <div className='flex flex-col gap-y-4 pb-[40px]'>
                {/* First Name */}
                <InputField
                      type='text'
                      id='firstNameRegister'
                      name='firstName'
                      defaultValue={dependenciesState['firstName']}
            
                      eventHandlers={{
                          onChange: (e) => {
                              // Handle change event
                              handleInputChange(e);
                          }
                      }}
                      required={true}
                      disabled={false}
                      label={first_name_label}
                      placeholder={first_name_placeholder}
                  />

                  {/* Last Name */}
                  <InputField
                      type='text'
                      id='lastNameRegister'
                      name='lastName'
                      defaultValue={dependenciesState['lastName']}
                      eventHandlers={{
                          onChange: (e) => {
                              // Handle change event
                              handleInputChange(e);
                          }
                      }}
                      required={true}
                      disabled={false}
                      label={last_name_label}
                      placeholder={last_name_placeholder}
                  />

                  {/* Email */}
                  <InputField
                      type='email'
                      id='emailRegister'
                      name='email'
                      defaultValue={dependenciesState['email']}
              
                      eventHandlers={{
                          onChange: (e) => {
                              // Handle change event
                              handleInputChange(e);
                          }
                      }}
                      required={true}
                      disabled={false}
                      label={email_label}
                      placeholder={email_placeholder}
                  />

                  {/* Phone */}
                  <div>
                    <InputField
                        type='tel'
                        className="w-full"
                        id='phoneRegister'
                        name='phone'
                        defaultValue={dependenciesState['phone']}
                        eventHandlers={{
                            onChange: handleInputChange
                        }}
                        required={true}
                        disabled={false}
                        label={phone_label}
                        placeholder={`${phone_placeholder}`}
                    />
                    {phoneError && (
                      <Paragraph
                          type='errors b4 text-red-600'
                          text={phoneError}
                      />
                    )}
                  </div>

                  {/* Password */}
                  <InputField
                      type='password'
                      id='passwordRegister'
                      name='password'
                      defaultValue={dependenciesState['password']}

                      eventHandlers={{
                          onChange: (e) => {
                              // Handle change event
                              handleInputChange(e);
                          }
                      }}
                      required={false}
                      disabled={false}
                      label={password_label}
                      placeholder={password_placeholder}
                  />

                  {/* PasswordConfirmation */}
                  <div>
                    <InputField
                        type='password'
                        id='passwordConfirmationRegister'
                        name='passwordConfirmation'
                        defaultValue={dependenciesState['passwordConfirmation']}
                        eventHandlers={{
                            onChange: (e) => {
                                // Handle change event
                                handleInputChange(e);
                            }
                        }}
                        required={!!dependenciesState['password']}
                        disabled={false}
                        label={password_confirm_label}
                        placeholder={password_confirmation_placeholder}
                    />
                    {passwordConfirmError && (
                      <Paragraph
                          type='errors b4 text-red-600'
                          text={passwordConfirmError}
                      />
                    )}
                  </div>

                  {/* Custom Fields */}
                  {showCustomFields && (
                      <CustomFields
                          customSignUpFields={customSignUpFields}
                          onChange={handleInputChange}
                          values={dependenciesState}
                          fieldsWithError={updateCustomerFieldsWithError}
                      />
                  )}

                  {(updateCustomerSuccess && !hasNewChangesAfterSuccess) && (
                      <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">
                              {success_message}
                            </p>
                          </div>
                        </div>
                      </div>
                  )}

                  {updateCustomerError && (
                      <Paragraph
                          type='errors text-center'
                          text={updateCustomerError}
                      />
                  )}

                  <div className='flex gap-2'>
                    <ButtonSpinner
                        classes={`flex-1 button button-${button_style} h-[calc(3rem-2px)] text-base`}
                        onClick={handleEditToggle}
                        disabled={updateCustomerLoading || !isUpdated || passwordError || passwordConfirmError || phoneError || (updateCustomerSuccess && !hasNewChangesAfterSuccess)}
                        ariaLabel='Save profile changes'
                        isLoading={updateCustomerLoading}
                        loadingText='Updating...'
                    >
                      {(updateCustomerSuccess && !hasNewChangesAfterSuccess) ? 'Saved Successfully' : 'Save Changes'}
                    </ButtonSpinner>
                    <Button
                        className='flex-1 button button-tertiary'
                        onClick={() => {
                          setIsEditMode(false);
                          setIsUpdated(false);
                          setPasswordError('');
                          setPasswordConfirmError('');
                          setPhoneError('');
                        }}
                        disabled={updateCustomerLoading}
                        aria-label={(updateCustomerSuccess && !hasNewChangesAfterSuccess) ? 'Go back to profile view' : 'Cancel editing'}
                        label={(updateCustomerSuccess && !hasNewChangesAfterSuccess) ? 'Go Back' : 'Cancel'}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>

          {false && <div className={`flex flex-col w-full lg:max-w-[307px]`}>
            <h4>CONTACT US</h4>
            <p className={`b2 pt-4 lg:pt-1`}>Contact customer care for further assistance.</p>
            <p className="b2 pt-[17px]">
              P - <a href="tel:+61418114745" className="cursor-pointer">(+61) 418 114 745</a>
            </p>
            <p className="b2 pt-2">
              E - <a href="mailto:online@leemathews.com.au" className="cursor-pointer">online@leemathews.com.au</a>
            </p>
          </div>}
        </div>
      </AccountSectionLayout>
  );
};

export default AccountProfileForm;
